import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import paymentsService from '../api/paymentsService';
import { AuthContext } from '../context/AuthContextProvider';

const usePaymentConfiguration = () => {
  const { negocioActivo } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    if (negocioActivo) {
      fetchConfiguration();
    }
  }, [negocioActivo]);

  const fetchConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const response = await paymentsService.getConfiguration();
      const config = response.data.paymentConfiguration;
      setConfiguration(config);
      setIsConfigured(config !== null);
    } catch (error) {
      console.error('Error fetching payment configuration:', error);
      toast.error('Error al cargar la configuración de pagos');
    } finally {
      setLoading(false);
    }
  }, []);

  const getAuthorizationUrl = useCallback(async () => {
    setLoading(true);
    try {
      const response = await paymentsService.getAuthorizationUrl();
      const url = response.data.url;
      // Redirigir al usuario a la URL de autorización
      window.location.href = url;
    } catch (error) {
      console.error('Error getting authorization URL:', error);
      toast.error('Error al obtener la URL de autorización');
      setLoading(false);
    }
  }, []);

  const linkPayment = useCallback(async (code, state) => {
    setLoading(true);
    try {
      await paymentsService.linkPayment({ code, state });
      toast.success('Cuenta de Mercado Pago vinculada exitosamente');
      // Refrescar configuración después del link exitoso
      const response = await paymentsService.getConfiguration();
      const config = response.data.paymentConfiguration;
      setConfiguration(config);
      setIsConfigured(config !== null);
    } catch (error) {
      console.error('Error linking payment:', error);
      toast.error('Error al vincular la cuenta de Mercado Pago');
    } finally {
      setLoading(false);
    }
  }, []);

  const unlinkPayment = useCallback(async () => {
    const confirmed = window.confirm(
      '¿Estás seguro de que querés quitar la configuración de pagos con tarjeta? Los clientes no podrán pagar con tarjeta en tu negocio a través de Faster.'
    );
    
    if (!confirmed) return;

    setLoading(true);
    try {
      await paymentsService.unlinkPayment();
      toast.success('Configuración de pagos removida exitosamente');
      // Refrescar configuración después del unlink exitoso
      const response = await paymentsService.getConfiguration();
      const config = response.data.paymentConfiguration;
      setConfiguration(config);
      setIsConfigured(config !== null);
    } catch (error) {
      console.error('Error unlinking payment:', error);
      toast.error('Error al quitar la configuración de pagos');
    } finally {
      setLoading(false);
    }
  }, []);

  const getPartialPublicKey = () => {
    if (!configuration?.public_key) return '';
    const key = configuration.public_key;
    return `${key.substring(0, 10)}...${key.substring(key.length - 10)}`;
  };

  return {
    loading,
    configuration,
    isConfigured,
    fetchConfiguration,
    getAuthorizationUrl,
    linkPayment,
    unlinkPayment,
    getPartialPublicKey,
  };
};

export default usePaymentConfiguration; 